export const PARENT_FINANCIAL_AID_ATTESTATION_KEYS = [
  'certifiedAccurate',
  'authorizedVerification',
  'incomeChangeAgreement',
] as const;

export type ParentFinancialAidAttestationKey =
  typeof PARENT_FINANCIAL_AID_ATTESTATION_KEYS[number];

export const PARENT_FINANCIAL_AID_DEFAULT_ATTESTATION_TEXT: Record<
  ParentFinancialAidAttestationKey,
  string
> = {
  certifiedAccurate: 'I attest that the information provided is true and complete.',
  authorizedVerification: 'I authorize verification with employers and banks.',
  incomeChangeAgreement: 'I agree to report income changes within 30 days.',
};

// Schools may replace any certification statement via
// financialAid.certificationStatements in staff settings (e.g. wording that
// names the school or its business office). Blank overrides fall back to the
// defaults above.
export const resolveParentFinancialAidAttestationText = (
  certificationStatements: Record<string, string> | undefined,
  key: ParentFinancialAidAttestationKey,
): string => {
  const override = String(certificationStatements?.[key] ?? '').trim();
  return override || PARENT_FINANCIAL_AID_DEFAULT_ATTESTATION_TEXT[key];
};

// Emits keys in canonical attestation order so serialized signatures of
// equal-content records always match.
export const sanitizeFinancialAidCertificationStatements = (
  value: unknown,
): Record<string, string> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const record = value as Record<string, unknown>;
  const sanitized: Record<string, string> = {};
  for (const key of PARENT_FINANCIAL_AID_ATTESTATION_KEYS) {
    const statement = String(record[key] ?? '').trim();
    if (statement) sanitized[key] = statement;
  }
  return sanitized;
};
